import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Select, Typography } from 'antd';
import {
  BellOutlined,
  CloseCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Header, Button } from '../../../../../core/components';
import { JobApplication } from '../models/job-application.model';
import {
  PipelineColumn,
  formatStageLabel,
} from '../utils/pipeline-board.utils';
import { StageWithTemplates } from '../../company/models/templates.model';
import { StagesService } from '../../company/services/stages.service';
import { JobApplicationStagesService } from '../services/job-application-stages-service';
import { UtilService } from '../../../../../core/services/util.service';
import {
  buildCandidateTemplateContext,
  hasRichTextContent,
  prepareNotificationTemplateForEditing,
} from '../utils/candidate-notification-template';

const { Text } = Typography;

const quillToolbar = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

export interface PipelineStageMoveRequest {
  application: JobApplication;
  targetColumn: PipelineColumn;
}

interface Props {
  open: boolean;
  pending: PipelineStageMoveRequest | null;
  companyId?: string;
  clientId?: string;
  onClose: () => void;
  onCompleted: () => void | Promise<void>;
}

const PipelineStageMoveModal: React.FC<Props> = ({
  open,
  pending,
  companyId,
  clientId,
  onClose,
  onCompleted,
}) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<StageWithTemplates | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const templateContext = useMemo(
    () => (pending ? buildCandidateTemplateContext(pending.application) : {}),
    [pending]
  );

  const resetState = useCallback(() => {
    setSelectedTemplate(null);
    setSelectedTemplateId('');
    setNote('');
    setSubmitting(false);
    setLoadingTemplates(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!open || !pending?.targetColumn.stageId || !companyId || !clientId) {
      return;
    }

    let cancelled = false;
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const result = await StagesService.GetById(
          companyId,
          clientId,
          pending.targetColumn.stageId!,
          false
        );
        if (!cancelled) {
          setSelectedTemplate(result ?? null);
        }
      } catch (error) {
        console.error(
          'Failed to load stage templates for pipeline move:',
          error
        );
        if (!cancelled) {
          setSelectedTemplate(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingTemplates(false);
        }
      }
    };

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [clientId, companyId, open, pending]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const templateOptions = useMemo(() => {
    if (!selectedTemplate?.notificationTemplates?.length) {
      return [];
    }
    return selectedTemplate.notificationTemplates.map((template) => ({
      value: template.id,
      label: template.name,
    }));
  }, [selectedTemplate]);

  const canEditMessage = Boolean(selectedTemplateId);
  const notifyDisabled =
    !selectedTemplateId ||
    !hasRichTextContent(note) ||
    submitting ||
    loadingTemplates;

  const onTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = selectedTemplate?.notificationTemplates?.find(
      (item) => item.id === templateId
    );
    if (template) {
      setNote(
        prepareNotificationTemplateForEditing(
          template.template || '',
          templateContext
        )
      );
    }
  };

  const submitMove = async (notifiable: boolean) => {
    if (!pending?.targetColumn.stageId) {
      UtilService.Alert(
        'Important!',
        'info',
        'Target stage is not available for moves.'
      );
      return;
    }

    if (notifiable && !hasRichTextContent(note)) {
      UtilService.Alert(
        'Important!',
        'info',
        'Enter a message to the candidate before notifying.'
      );
      return;
    }

    setSubmitting(true);
    try {
      const notificationId =
        notifiable && selectedTemplateId ? selectedTemplateId : '';
      const useEditedMessage = Boolean(
        notificationId && hasRichTextContent(note)
      );
      await JobApplicationStagesService.AddStage(
        pending.application.id,
        pending.targetColumn.stageId,
        note,
        notificationId,
        useEditedMessage
      );
      UtilService.Alert(
        'Success!',
        'success',
        notifiable
          ? `Moved to ${formatStageLabel(pending.targetColumn.key)} and notification queued.`
          : `Moved to ${formatStageLabel(pending.targetColumn.key)}.`
      );
      handleClose();
      await onCompleted();
    } catch (error) {
      console.error('Failed to move application on pipeline board:', error);
      UtilService.Alert(
        'Error',
        'error',
        'Could not update application stage. Try again from the application detail page.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!pending) {
    return null;
  }

  const candidateLabel =
    pending.application.candidate ||
    pending.application.fullname ||
    'Candidate';
  const targetLabel = formatStageLabel(pending.targetColumn.key);

  return (
    <ReactModal
      isOpen={open}
      onRequestClose={handleClose}
      className="bg-white rounded-lg p-6 max-w-lg mx-auto shadow-lg w-full scrollbar-hide"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
      ariaHideApp={false}
    >
      <Header
        title="Move application"
        buttons={[
          {
            icon: { value: <CloseCircleOutlined />, size: 'text-lg' },
            text: { size: 'text-sm', value: 'Close', weight: 500 },
            color: 'red',
            tone: 500,
            onClick: handleClose,
          },
        ]}
      />

      <div className="mb-4 text-left">
        <Text className="block text-sm text-gray-700">
          <strong>{candidateLabel}</strong> → <strong>{targetLabel}</strong>
        </Text>
        <Text type="secondary" className="block text-xs mt-1">
          Optional: pick a notification template, edit the message, then notify
          the candidate. You can also move without sending a notification.
        </Text>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-5">
        <Select
          placeholder={
            loadingTemplates
              ? 'Loading templates…'
              : 'Notification template (optional)'
          }
          style={{ width: '100%', height: '32px' }}
          className="rounded-lg"
          value={selectedTemplateId || undefined}
          onChange={onTemplateChange}
          disabled={
            loadingTemplates ||
            !selectedTemplate?.notificationTemplates?.length ||
            submitting
          }
          loading={loadingTemplates}
          options={templateOptions}
          allowClear
          onClear={() => {
            setSelectedTemplateId('');
            setNote('');
          }}
        />

        <div className="space-y-2 text-left">
          {canEditMessage ? (
            <>
              <Text
                type="secondary"
                className="text-xs sm:text-sm block leading-relaxed"
              >
                Template text is a starting point only. Edit the message below —
                what you send will not change the stored templates.
              </Text>
              <ReactQuill
                theme="snow"
                value={note}
                onChange={setNote}
                modules={{ toolbar: quillToolbar }}
                placeholder="Edit the message to the candidate..."
                className="quill-custom"
                readOnly={submitting}
              />
            </>
          ) : (
            <ReactQuill
              theme="snow"
              value={note}
              readOnly
              modules={{ toolbar: false }}
              placeholder="Select a template to load a starter message you can edit before notifying."
              className="quill-readonly"
            />
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          icon={{ value: <SaveOutlined />, size: 'text-lg' }}
          text={{
            value: 'Move without notifying',
            weight: 500,
            size: 'text-sm',
          }}
          color="gray"
          tone={500}
          disabled={submitting}
          onClick={() => void submitMove(false)}
        />
        <Button
          icon={{ value: <BellOutlined />, size: 'text-lg' }}
          text={{ value: 'Notify & move', weight: 500, size: 'text-sm' }}
          color="blue"
          tone={500}
          disabled={notifyDisabled}
          onClick={() => void submitMove(true)}
        />
      </div>
    </ReactModal>
  );
};

export default PipelineStageMoveModal;
